import React, {ReactNode, ReactElement, useContext, useState} from 'react';
import {cx} from '@emotion/css';

import {ArticleDialogueWithCta, ContentfulContent, LinkProp} from '@fo/shared-types';
import DeviceContext, {Device} from '@fo/shared-providers/DeviceContext';
import getLinksFromContent from '@fo/root/contentful/getLinksFromContent';
import ArticleDialogueWithCTA from '@fo/designSystem/ArticleDialogueWithCta/ArticleDialogueWithCta';
import JumpToAnchorMenu from '../JumpToAnchorMenu/JumpToAnchorMenu';
import RichText from '../Text/RichText/RichText';
import addRefsToRichText from '../Text/RichText/addRefsToRichText';

import styles from './RichTextWithContents.styles';

interface Props {
  children?: ReactNode;
  content: ContentfulContent;
  dialogueWithCta?: ArticleDialogueWithCta;
}

function RichTextWithContents({children, content, dialogueWithCta}: Props): ReactElement {
  const contentWithRefs = content && addRefsToRichText(content);
  const anchorsLinks: LinkProp[] = getLinksFromContent(contentWithRefs.content);
  const shouldRenderDialogueWithCta =
    !!dialogueWithCta?.title && !!dialogueWithCta?.description && !!dialogueWithCta?.ctaText && !!dialogueWithCta?.slug;
  const [isDialogueOpen, setIsDialogueOpen] = useState<boolean>(shouldRenderDialogueWithCta);
  const {device} = useContext(DeviceContext);
  const isDesktop = device === Device.DESKTOP;
  const isHandheld = !isDesktop;

  const handleCloseDialogue = () => {
    setIsDialogueOpen(false);
  };

  const dialogueWithCtaDesktopEl = isDialogueOpen && shouldRenderDialogueWithCta && (
    <div className="col-md-3">
      <ArticleDialogueWithCTA
        title={dialogueWithCta.title}
        description={dialogueWithCta.description}
        ctaButtonLabel={dialogueWithCta.ctaText}
        href={dialogueWithCta.slug}
        isShowIcon
        isShowCloseButton
        onClose={handleCloseDialogue}
        shouldShowTrustpilot
      />
    </div>
  );

  const dialogueWithCtaMobileEl = shouldRenderDialogueWithCta && (
    <ArticleDialogueWithCTA ctaButtonLabel={dialogueWithCta.ctaText} href={dialogueWithCta.slug} />
  );

  const jumpMenu = (
    <JumpToAnchorMenu
      links={anchorsLinks}
      afterContent={isHandheld && shouldRenderDialogueWithCta && dialogueWithCtaMobileEl}
    />
  );

  return (
    <>
      {isDesktop && (
        <div className="container">
          <div className="row">
            {anchorsLinks.length > 0 ? (
              <>
                <div className="col-md-3">{jumpMenu}</div>
                <div className={isDialogueOpen ? 'col-md-6' : 'col-md-9'}>
                  <div className={cx(styles.content())}>
                    <RichText className={cx(styles.richText)} content={content} />
                    {children}
                  </div>
                </div>
                {dialogueWithCtaDesktopEl}
              </>
            ) : (
              <>
                <div className={isDialogueOpen ? 'col-md-9' : 'col-md-10 col-md-offset-1'}>
                  <div className={cx(styles.content())}>
                    <RichText className={cx(styles.richText)} content={content} />
                    {children}
                  </div>
                </div>
                {dialogueWithCtaDesktopEl}
              </>
            )}
          </div>
        </div>
      )}

      {isHandheld && (
        <>
          {anchorsLinks.length > 0 ? (
            jumpMenu
          ) : (
            <div className={cx(styles.dialogueWithCtaContainer)}>{dialogueWithCtaMobileEl}</div>
          )}
          <div className={cx(styles.content(anchorsLinks.length === 0 && !shouldRenderDialogueWithCta))}>
            <RichText className={cx(styles.richText)} content={content} />
            {children}
          </div>
        </>
      )}
    </>
  );
}

export default RichTextWithContents;
